// Game State for Multi-Layer Business Empire
class GameState {
    constructor() {
        this.money = 0;
        this.gems = 0;
        this.clickValue = 1;
        this.moneyPerSecond = 0;
        this.totalMoneyEarned = 0;
        this.totalClicks = 0;
        
        // Multi-layer prestige system (inspired by Pincremental)
        this.prestigeLayers = [
            {
                id: 'business',
                name: 'Business Points',
                points: 0,
                requirement: 100000, // $100K
                requirementType: 'money',
                multiplier: 0.1, // 10% per point
                resetLayers: []
            },
            {
                id: 'innovation',
                name: 'Innovation Points', 
                points: 0,
                requirement: 25, // 25 business points
                requirementType: 'business',
                multiplier: 0.5, // 50% per point
                resetLayers: ['business']
            },
            {
                id: 'legacy',
                name: 'Legacy Points',
                points: 0,
                requirement: 10, // 10 innovation points
                requirementType: 'innovation', 
                multiplier: 2.0, // 200% per point
                resetLayers: ['business', 'innovation']
            }
        ];
        
        // Enhanced upgrade system (inspired by Scratch Inc.)
        this.basicUpgrades = [
            {
                id: 'cursor_power',
                name: 'Better Cursor',
                description: 'Increases click value',
                baseCost: 10,
                cost: 10,
                level: 0,
                effect: 1,
                maxLevel: 100,
                type: 'click'
            },
            {
                id: 'cursor_speed',
                name: 'Faster Clicking',
                description: 'Increases clicking efficiency',
                baseCost: 50,
                cost: 50,
                level: 0,
                effect: 0.5,
                maxLevel: 50,
                type: 'click'
            },
            {
                id: 'auto_clicker',
                name: 'Auto Clicker',
                description: 'Automatically generates money',
                baseCost: 100,
                cost: 100,
                level: 0,
                effect: 1,
                maxLevel: 200,
                type: 'passive'
            },
            {
                id: 'click_multiplier',
                name: 'Click Multiplier',
                description: 'Multiplies all click income',
                baseCost: 500,
                cost: 500,
                level: 0,
                effect: 1.1,
                maxLevel: 25,
                type: 'multiplier'
            }
        ];
        
        this.businessUpgrades = [
            {
                id: 'lemonade_stand',
                name: 'Lemonade Stand',
                description: 'A simple business that generates money',
                baseCost: 100,
                cost: 100,
                level: 0,
                baseIncome: 2,
                unlocked: true,
                tier: 1
            },
            {
                id: 'coffee_shop',
                name: 'Coffee Shop',
                description: 'A popular coffee business',
                baseCost: 1000,
                cost: 1000,
                level: 0,
                baseIncome: 15,
                unlocked: false,
                tier: 2
            },
            {
                id: 'restaurant',
                name: 'Restaurant',
                description: 'A fancy dining establishment',
                baseCost: 10000,
                cost: 10000,
                level: 0,
                baseIncome: 100,
                unlocked: false,
                tier: 3
            },
            {
                id: 'tech_startup',
                name: 'Tech Startup',
                description: 'Innovative technology company',
                baseCost: 100000,
                cost: 100000,
                level: 0,
                baseIncome: 800,
                unlocked: false,
                tier: 4
            },
            {
                id: 'bank',
                name: 'Investment Bank',
                description: 'High-finance money machine',
                baseCost: 1000000,
                cost: 1000000,
                level: 0,
                baseIncome: 5000,
                unlocked: false,
                tier: 5
            },
            {
                id: 'mega_corp',
                name: 'Mega Corporation',
                description: 'Multinational conglomerate',
                baseCost: 10000000,
                cost: 10000000,
                level: 0,
                baseIncome: 30000,
                unlocked: false,
                tier: 6
            }
        ];
        
        this.premiumUpgrades = [
            {
                id: 'gem_generator',
                name: 'Gem Generator',
                description: 'Generates gems over time',
                cost: 10, // gems
                level: 0,
                effect: 0.1,
                currency: 'gems',
                type: 'gem_income'
            },
            {
                id: 'global_multiplier',
                name: 'Global Multiplier',
                description: 'Multiplies all income sources',
                cost: 25,
                level: 0,
                effect: 1.5,
                currency: 'gems',
                type: 'global_mult'
            },
            {
                id: 'prestige_boost',
                name: 'Prestige Accelerator',
                description: 'Increases prestige point gain',
                cost: 50,
                level: 0,
                effect: 1.2,
                currency: 'gems',
                type: 'prestige_boost'
            },
            {
                id: 'offline_earnings',
                name: 'Offline Manager',
                description: 'Earn money while away',
                cost: 100,
                level: 0,
                effect: 0.25, // 25% of normal rate
                currency: 'gems',
                type: 'offline'
            }
        ];
        
        // Settings
        this.settings = {
            soundEnabled: true,
            autoSaveEnabled: true,
            notifications: true
        };
        
        this.loadGame();
        this.calculateAll();
    }
    
    // Prestige system with multiple layers
    canPrestige(layerId) {
        const layer = this.prestigeLayers.find(l => l.id === layerId);
        if (!layer) return false;
        
        switch (layer.requirementType) {
            case 'money':
                return this.totalMoneyEarned >= layer.requirement;
            case 'business':
                return this.prestigeLayers[0].points >= layer.requirement;
            case 'innovation':
                return this.prestigeLayers[1].points >= layer.requirement;
            default:
                return false;
        }
    }
    
    getPrestigeGain(layerId) {
        if (!this.canPrestige(layerId)) return 0;
        
        const layer = this.prestigeLayers.find(l => l.id === layerId);
        if (!layer) return 0;
        
        switch (layer.requirementType) {
            case 'money':
                return Math.floor(Math.sqrt(this.totalMoneyEarned / layer.requirement));
            case 'business':
                return Math.floor(this.prestigeLayers[0].points / layer.requirement);
            case 'innovation':
                return Math.floor(this.prestigeLayers[1].points / layer.requirement);
            default:
                return 0;
        }
    }
    
    doPrestige(layerId) {
        if (!this.canPrestige(layerId)) return false;
        
        const layer = this.prestigeLayers.find(l => l.id === layerId);
        if (!layer) return false;
        
        const gain = this.getPrestigeGain(layerId);
        layer.points += gain;
        
        // Reset based on layer
        if (layerId === 'business') {
            this.money = 0;
            this.totalMoneyEarned = 0;
            this.basicUpgrades.forEach(upgrade => {
                upgrade.level = 0;
                upgrade.cost = upgrade.baseCost;
            });
            this.businessUpgrades.forEach(upgrade => {
                upgrade.level = 0;
                upgrade.cost = upgrade.baseCost;
                upgrade.unlocked = upgrade.tier === 1;
            });
        } else if (layerId === 'innovation') {
            // Reset business layer points and everything below
            this.prestigeLayers[0].points = 0;
            this.doPrestige('business'); // Trigger business reset
        } else if (layerId === 'legacy') {
            // Reset innovation and business layers
            this.prestigeLayers[0].points = 0;
            this.prestigeLayers[1].points = 0;
            this.doPrestige('business');
        }
        
        this.calculateAll();
        this.saveGame();
        return true;
    }
    
    // Enhanced calculation system
    calculateAll() {
        this.calculateClickValue();
        this.calculateMoneyPerSecond();
        this.unlockBusinesses();
    }
    
    calculateClickValue() {
        let clickValue = 1;
        
        // Basic upgrades
        this.basicUpgrades.forEach(upgrade => {
            if (upgrade.type === 'click' && upgrade.level > 0) {
                clickValue += upgrade.effect * upgrade.level;
            }
        });
        
        // Multiplier upgrades
        this.basicUpgrades.forEach(upgrade => {
            if (upgrade.type === 'multiplier' && upgrade.level > 0) {
                clickValue *= Math.pow(upgrade.effect, upgrade.level);
            }
        });
        
        // Apply prestige bonuses
        this.prestigeLayers.forEach(layer => {
            if (layer.points > 0) {
                clickValue *= (1 + layer.points * layer.multiplier);
            }
        });
        
        // Premium upgrades
        this.premiumUpgrades.forEach(upgrade => {
            if (upgrade.type === 'global_mult' && upgrade.level > 0) {
                clickValue *= Math.pow(upgrade.effect, upgrade.level);
            }
        });
        
        this.clickValue = clickValue;
    }
    
    calculateMoneyPerSecond() {
        let mps = 0;
        
        // Auto clickers
        this.basicUpgrades.forEach(upgrade => {
            if (upgrade.type === 'passive' && upgrade.level > 0) {
                mps += upgrade.effect * upgrade.level;
            }
        });
        
        // Business income
        this.businessUpgrades.forEach(business => {
            if (business.level > 0) {
                mps += business.baseIncome * business.level;
            }
        });
        
        // Apply prestige bonuses
        this.prestigeLayers.forEach(layer => {
            if (layer.points > 0) {
                mps *= (1 + layer.points * layer.multiplier);
            }
        });
        
        // Premium upgrades
        this.premiumUpgrades.forEach(upgrade => {
            if (upgrade.type === 'global_mult' && upgrade.level > 0) {
                mps *= Math.pow(upgrade.effect, upgrade.level);
            }
        });
        
        this.moneyPerSecond = mps;
    }
    
    unlockBusinesses() {
        this.businessUpgrades.forEach((business, index) => {
            if (index === 0) {
                business.unlocked = true;
            } else {
                const prevBusiness = this.businessUpgrades[index - 1];
                // Unlock if previous business has 10+ levels OR player has enough money to afford this business
                business.unlocked = prevBusiness.level >= 10 || this.totalMoneyEarned >= business.baseCost;
            }
        });
    }
    
    // Upgrade purchase methods
    buyBasicUpgrade(upgradeId) {
        const upgrade = this.basicUpgrades.find(u => u.id === upgradeId);
        if (!upgrade || this.money < upgrade.cost || upgrade.level >= upgrade.maxLevel) return false;
        
        this.money -= upgrade.cost;
        upgrade.level++;
        upgrade.cost = Math.floor(upgrade.baseCost * Math.pow(1.15, upgrade.level));
        
        this.calculateAll();
        return true;
    }
    
    buyBusinessUpgrade(upgradeId) {
        const upgrade = this.businessUpgrades.find(u => u.id === upgradeId);
        if (!upgrade || this.money < upgrade.cost || !upgrade.unlocked) return false;
        
        this.money -= upgrade.cost;
        upgrade.level++;
        upgrade.cost = Math.floor(upgrade.baseCost * Math.pow(1.2, upgrade.level));
        
        this.calculateAll();
        return true;
    }
    
    buyPremiumUpgrade(upgradeId) {
        const upgrade = this.premiumUpgrades.find(u => u.id === upgradeId);
        if (!upgrade) return false;
        
        if (upgrade.currency === 'gems' && this.gems < upgrade.cost) return false;
        
        this.gems -= upgrade.cost;
        upgrade.level++;
        upgrade.cost = Math.floor(upgrade.cost * 2); // Premium upgrades get expensive
        
        this.calculateAll();
        return true;
    }
    
    // Gem generation
    generateGems() {
        // Gems generated from total money earned
        const baseGemRate = Math.floor(this.totalMoneyEarned / 10000); // 1 gem per 10K earned
        
        // Premium gem generators
        let gemRate = baseGemRate;
        this.premiumUpgrades.forEach(upgrade => {
            if (upgrade.type === 'gem_income' && upgrade.level > 0) {
                gemRate += upgrade.effect * upgrade.level;
            }
        });
        
        return gemRate;
    }
    
    // Save/Load system
    saveGame() {
        const saveData = {
            money: this.money,
            gems: this.gems,
            clickValue: this.clickValue,
            moneyPerSecond: this.moneyPerSecond,
            totalMoneyEarned: this.totalMoneyEarned,
            totalClicks: this.totalClicks,
            prestigeLayers: this.prestigeLayers,
            basicUpgrades: this.basicUpgrades,
            businessUpgrades: this.businessUpgrades,
            premiumUpgrades: this.premiumUpgrades,
            settings: this.settings,
            lastSave: Date.now()
        };
        
        localStorage.setItem('rndm-inc-empire-save', JSON.stringify(saveData));
    }
    
    loadGame() {
        const saveData = localStorage.getItem('rndm-inc-empire-save');
        if (saveData) {
            try {
                const data = JSON.parse(saveData);
                
                this.money = data.money || 0;
                this.gems = data.gems || 0;
                this.clickValue = data.clickValue || 1;
                this.moneyPerSecond = data.moneyPerSecond || 0;
                this.totalMoneyEarned = data.totalMoneyEarned || 0;
                this.totalClicks = data.totalClicks || 0;
                
                // Merge prestige layers
                if (data.prestigeLayers) {
                    data.prestigeLayers.forEach((savedLayer, index) => {
                        if (this.prestigeLayers[index]) {
                            this.prestigeLayers[index].points = savedLayer.points || 0;
                        }
                    });
                }
                
                // Merge upgrades
                if (data.basicUpgrades) {
                    data.basicUpgrades.forEach(savedUpgrade => {
                        const upgrade = this.basicUpgrades.find(u => u.id === savedUpgrade.id);
                        if (upgrade) {
                            upgrade.level = savedUpgrade.level;
                            upgrade.cost = savedUpgrade.cost;
                        }
                    });
                }
                
                if (data.businessUpgrades) {
                    data.businessUpgrades.forEach(savedUpgrade => {
                        const upgrade = this.businessUpgrades.find(u => u.id === savedUpgrade.id);
                        if (upgrade) {
                            upgrade.level = savedUpgrade.level;
                            upgrade.cost = savedUpgrade.cost;
                            upgrade.unlocked = savedUpgrade.unlocked;
                        }
                    });
                }
                
                if (data.premiumUpgrades) {
                    data.premiumUpgrades.forEach(savedUpgrade => {
                        const upgrade = this.premiumUpgrades.find(u => u.id === savedUpgrade.id);
                        if (upgrade) {
                            upgrade.level = savedUpgrade.level;
                            upgrade.cost = savedUpgrade.cost;
                        }
                    });
                }
                
                if (data.settings) {
                    this.settings = { ...this.settings, ...data.settings };
                }
                
                // Calculate offline earnings
                if (data.lastSave && this.moneyPerSecond > 0) {
                    const offlineTime = Math.min((Date.now() - data.lastSave) / 1000, 7200); // Max 2 hours
                    let offlineRate = this.moneyPerSecond;
                    
                    // Check for offline manager
                    const offlineManager = this.premiumUpgrades.find(u => u.id === 'offline_earnings');
                    if (offlineManager && offlineManager.level > 0) {
                        offlineRate *= Math.pow(offlineManager.effect, offlineManager.level);
                    } else {
                        offlineRate *= 0.1; // Only 10% without offline manager
                    }
                    
                    const offlineEarnings = offlineRate * offlineTime;
                    if (offlineEarnings > 0) {
                        this.money += offlineEarnings;
                        this.totalMoneyEarned += offlineEarnings;
                        this.showOfflineEarnings(offlineEarnings, offlineTime);
                    }
                }
                
            } catch (e) {
                console.error('Error loading save data:', e);
            }
        }
    }
    
    showOfflineEarnings(earnings, time) {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        const timeString = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
        
        alert(`Welcome back! You were away for ${timeString} and earned $${this.formatNumber(earnings)}!`);
    }
    
    resetGame() {
        if (confirm('Are you sure you want to reset all progress? This cannot be undone!')) {
            localStorage.removeItem('rndm-inc-empire-save');
            location.reload();
        }
    }
    
    formatNumber(num) {
        if (num < 1000) return Math.floor(num).toString();
        if (num < 1000000) return (num / 1000).toFixed(1) + 'K';
        if (num < 1000000000) return (num / 1000000).toFixed(1) + 'M';
        if (num < 1000000000000) return (num / 1000000000).toFixed(1) + 'B';
        if (num < 1000000000000000) return (num / 1000000000000).toFixed(1) + 'T';
        return (num / 1000000000000000).toFixed(1) + 'Q';
    }
}

export default GameState;
